
import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";

const AdjustDiscount = ({
  isOpen,
  onClose,
  billData,
  onAdjustmentComplete,
}) => {
  const [items, setItems] = useState([]);
  const [adjustments, setAdjustments] = useState({});

  useEffect(() => {
    if (billData && isOpen) {
      setItems(billData.items || []);
      setAdjustments({});
    }
  }, [billData, isOpen]);

  const handleDiscountChange = (unitId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = items.find((i) => i.UnitId === unitId);
    const maxAllowed = item ? item.UnitPrice : 0;

    setAdjustments((prev) => ({
      ...prev,
      [unitId]: Math.min(numValue, maxAllowed),
    }));
  };

  const totalAppliedDiscount = Object.values(adjustments).reduce(
    (sum, val) => sum + (val || 0),
    0,
  );

  const remainingDiscount = Math.max(
    0,
    (billData?.discountAmount || 0) - totalAppliedDiscount,
  );

  const handleSave = () => {
    if (totalAppliedDiscount > (billData?.discountAmount || 0)) {
      Swal.fire(
        "Error",
        "Total adjustment cannot exceed the given discount",
        "error",
      );
      return;
    }

    if (totalAppliedDiscount === 0) {
      Swal.fire(
        "Warning",
        "Please apply discount to at least one item",
        "warning",
      );
      return;
    }

    const finalAdjustedItems = items.map((item) => ({
      ...item,
      discountApplied: adjustments[item.UnitId] || 0,
      finalPrice: item.UnitPrice - (adjustments[item.UnitId] || 0),
    }));

    Swal.fire({
      title: "Success!",
      text: "Discount adjusted successfully.",
      icon: "success",
    }).then(() => {
      onAdjustmentComplete(finalAdjustedItems); 
      onClose();
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .offcanvas-backdrop { opacity: 0; transition: opacity 0.3s ease; }
        .offcanvas-backdrop.show { opacity: 0.5; }
        @media (max-width: 576px)    { #offcanvasAdjustDiscount { width: 90% !important; } }
        @media (min-width: 577px) and (max-width: 992px)  { #offcanvasAdjustDiscount { width: 55% !important; } }
        @media (min-width: 993px) and (max-width: 1440px) { #offcanvasAdjustDiscount { width: 40% !important; } }
        @media (min-width: 1441px)   { #offcanvasAdjustDiscount { width: 30% !important; } }
      `}</style>

      <div
        className={`offcanvas-backdrop fade ${isOpen ? "show" : ""}`}
        onClick={onClose}
      />

      <div
        id="offcanvasAdjustDiscount"
        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
      >
        <div className="offcanvas-header">
          <h4>Adjust Discount </h4>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>
        <hr />

        <div className="offcanvas-body">
          <div className="row mb-4">
            <div className="col-6">
              <strong>Billed Amount</strong>
              <h4 className="text-success">₹{billData?.billedAmount || 0}</h4>
            </div>
            <div className="col-6 text-end">
              <strong>Discount </strong>
              <h4 className="text-primary">₹{billData?.discountAmount || 0}</h4>
            </div>
          </div>

          <div className="mb-3 text-end">
            <strong>Remaining :</strong>{" "}
            <span className="text-danger fw-bold">₹{remainingDiscount}</span>
          </div>

          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-end">Original Price</th>
                <th className="text-end">Discount </th>
                <th className="text-end">Final Price</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const discount = adjustments[item.UnitId] || 0;
                const finalPrice = Math.max(0, item.UnitPrice - discount);

                return (
                  <tr key={item.UnitId}>
                    <td>{item.ProductName}</td>
                    <td className="text-end">₹{item.UnitPrice}</td>
                    <td>
                      <input
                        type="number"
                        className="form-control text-end"
                        value={discount === 0 ? "" : discount}
                        onFocus={() => {
                          if (discount === 0) {
                            handleDiscountChange(item.UnitId, "");
                          }
                        }}
                        onChange={(e) =>
                          handleDiscountChange(item.UnitId, e.target.value)
                        }
                        min="0"
                        max={item.UnitPrice}
                      />
                    </td>
                    <td className="text-end fw-bold">
                      ₹{finalPrice.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

AdjustDiscount.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  billData: PropTypes.shape({
    billedAmount: PropTypes.number,
    discountAmount: PropTypes.number,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        UnitId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        ProductName: PropTypes.string.isRequired,
        UnitPrice: PropTypes.number.isRequired,
      }),
    ),
  }).isRequired,
  onAdjustmentComplete: PropTypes.func.isRequired,
};

export default AdjustDiscount;
